import { useClaudeFleetStore } from '@/stores/claude-fleet.store'
import { useClaudeFleetHandBackStore } from '@/stores/claude-fleet-hand-back.store'

import { useConfirm } from '@/composables/useConfirm'
import { useToast } from '@/composables/useToast'

import { handBackConfirmText } from '@/services/claude-fleet'
import type { FleetHandBackCheck, FleetHandBackMode } from '@/services/claude-fleet'

const HEADLINES: Record<FleetHandBackMode, { title: string; confirmText: string }> = {
  anyone: { title: 'Hand back the run', confirmText: 'Hand back' },
  mine: { title: 'Run it on my runner', confirmText: 'Run it on mine' }
}

/**
 * Hand back and Run it on mine for one run, shared by every place a run has a ⋮ menu: the menu asks the fleet who
 * may when it opens, a choice asks first in the fleet's words for the job, and the run lists read again after.
 */
export function useFleetRunHandBack() {
  const fleetStore = useClaudeFleetStore()
  const handBackStore = useClaudeFleetHandBackStore()
  const { confirmDialog } = useConfirm()
  const { showSuccess, showError } = useToast()

  function checkOf(runId: string): FleetHandBackCheck {
    return handBackStore.checkOf(runId) ?? { status: 'loading' }
  }

  function open(runId: string) {
    void handBackStore.check(runId)
  }

  async function choose(runId: string, mode: FleetHandBackMode) {
    if (handBackStore.pending[runId]) return
    const check = handBackStore.checkOf(runId)
    if (check?.status !== 'ready') return
    const confirmed = await confirmDialog({
      ...HEADLINES[mode],
      message: handBackConfirmText(check.eligibility, mode),
      confirmColor: 'primary'
    })
    if (!confirmed) return
    try {
      const result = await handBackStore.handBack(runId, mode)
      showSuccess(result.text, 'Handed back')
    } catch {
      showError(handBackStore.error ?? 'The run was not handed back', 'Not handed back')
      handBackStore.clearError()
    }
    await refresh(runId)
  }

  async function refresh(runId: string) {
    await fleetStore.fetchRuns({ background: true })
    if (fleetStore.selectedRun?.runId === runId) await fleetStore.fetchRun(runId, { background: true })
  }

  return { checkOf, open, choose }
}
